<template>
  <section class="plan-section" :id="blockId || undefined">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <section-title data-aos="fade-up" :kicker="kicker" :title="title" :lede="lede" />
          <div class="plan-card" data-aos="fade-up">
            <!-- Below md the reader picks a plan instead of scrolling sideways
                 through four columns. The label column stays and the plan
                 columns that are not chosen are hidden by `.is-inactive-col`.
                 It is hidden above md rather than not rendered, because there
                 the full table shows and this control has nothing to choose. -->
            <div class="plan-switch">
              <segmented-switch
                v-model="activeIndex"
                block
                :options="columnNames"
              />
            </div>

            <div class="table-shadow">
              <div class="table-scroll">
                <table>
                  <thead>
                    <tr class="plan-meta">
                      <th class="plan-corner" aria-hidden="true"></th>
                      <th
                        v-for="(column, index) in columns"
                        :key="'meta-' + index"
                        :class="colClass(index, column)"
                      >
                        <span v-if="column.badge" class="plan-badge">{{ column.badge }}</span>
                        <div class="plan-meta-inner">
                          <div class="plan-head">
                            <span v-if="column.icon" class="plan-icon">
                              <font-awesome-icon :icon="column.icon" />
                            </span>
                            <div class="plan-head-copy">
                              <span v-if="column.audience" class="plan-audience">{{ column.audience }}</span>
                              <span class="plan-name">{{ column.name }}</span>
                            </div>
                          </div>
                          <p v-if="column.blurb" class="plan-blurb">{{ column.blurb }}</p>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="priceLabel" class="plan-price-row">
                      <th>
                        <strong>{{ priceLabel }}</strong>
                        <span v-if="priceHint">{{ priceHint }}</span>
                      </th>
                      <td
                        v-for="(column, index) in columns"
                        :key="'price-' + index"
                        :class="colClass(index, column)"
                      >
                        <div class="plan-cost">
                          <span class="plan-price">{{ column.price }}</span>
                          <span class="plan-unit">{{ column.unit }}</span>
                          <!-- The other billing period, where a plan is sold both
                               ways. Both prices on screen answer the question the
                               billing switch used to, without a control: the
                               annual rate stays the headline and the monthly one
                               is a note under it. -->
                          <span v-if="column.alt" class="plan-alt">{{ column.alt }}</span>
                        </div>
                      </td>
                    </tr>
                    <tr v-for="(row, rowIndex) in rows" :key="rowIndex">
                      <th>
                        <strong>{{ row.label }}</strong>
                        <span v-if="row.hint">{{ row.hint }}</span>
                      </th>
                      <td
                        v-for="(cell, cellIndex) in row.cells"
                        :key="cellIndex"
                        :class="colClass(cellIndex, columns[cellIndex])"
                      >
                        <span v-if="cell.type === 'yes'" class="plan-mark plan-mark--yes" aria-hidden="true">
                          <font-awesome-icon icon="check" />
                        </span>
                        <span v-else-if="cell.type === 'no'" class="plan-mark plan-mark--no" aria-hidden="true">
                          <font-awesome-icon icon="times" />
                        </span>
                        <span
                          v-else
                          class="plan-cell-text"
                          :class="{ 'is-accent': cell.accent }"
                        >{{ cell.value }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <ul v-if="remarksList.length" class="plan-remarks">
              <li v-for="(remark, remarkIndex) in remarksList" :key="remarkIndex">{{ remark }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.plan-section {
  background: $ground-tint;
}

/* Grid line: 2px wide so it reads as a deliberate rule, cool and light so it
   never feels like a heavy table border. Cells draw only their right and
   bottom edge, so shared lines stay a single 2px rule instead of doubling. */

/* The table's shadow is painted outside the table's own box. A scroll
   container clips ink at its padding box, so the shadow must sit on a wrapper
   that is not itself a scroll container - otherwise the tail is severed in a
   hard line wherever the clip happens to land. Reserving padding inside the
   scroll box cannot work here: the blur's tail reaches roughly $offset + 3 x
   sigma, far more than any sane padding, and the left and right tails get cut
   the same way. */
$plan-remarks-gap: 1.75rem;

.plan-card {
  background: transparent;
  overflow: visible;
}

.table-shadow {
  filter: drop-shadow($shadow-lift);
}

/* The wrapper (overflow, touch scrolling) comes from `_table.scss`. Only the
   padding the plan badge needs is local: the badge hangs half its height above
   the table's top edge and would otherwise be clipped by the scroll container. */
.table-scroll {
  padding-top: 1.85rem;
}

/* This is the one table that overrides the shared model rather than adopting it.
   A pricing table is a grid of separate cells so the highlighted column can carry
   its own radius and fill, so the collapse model, the 2px rule and the 28% label
   column are deliberately local. Everything shareable is shared: the padding
   (*.75rem 1rem*), the rule colour (*$line-blue*), the row hover tint
   (*$ground-tint*) and the `.table-scroll` wrapper. */
.plan-card table {
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  min-width: 100%;
  width: 100%;
}

th,
td {
  background: $ground;
  border-bottom: 2px solid $line-blue;
  border-right: 2px solid $line-blue;
  padding: 0.75rem 1rem;
  transition: background-color $dur-quick $ease;
  vertical-align: middle;
}

/* Row hover. The label column carries a hint paragraph, so reading across four
   columns is real work; tinting the whole row is the cheapest way to hold the
   eye on one line. The "Most Popular" column is a solid blue fill, so it is
   excluded - a tint would not show on it, and the descendant selector would
   otherwise outrank the is-hit rule and wash the column out. */
tbody tr:hover {
  th,
  td:not(.is-hit) {
    background: $ground-tint;
  }

  /* The ticks and crosses are what the row is actually read for, so they take
     the emphasis while the row is under the pointer. */
  .plan-mark {
    transform: scale(1.1);
  }
}

/* The scroll container is keyboard-focusable once it overflows, so show where
   focus landed. */
.table-scroll:focus-visible {
  border-radius: $r-lg;
  outline: 3px solid $ring-focus;
  outline-offset: 3px;
}

/* Close the leading edges the right/bottom rules leave open. */
thead th:not(.plan-corner) {
  border-top: 2px solid $line-blue;
}

thead tr.plan-meta th:nth-child(2),
tbody th {
  border-left: 2px solid $line-blue;
}

thead th {
  background: $ground;
  color: $text-strong;
  font-family: $headings-font-family;
  font-weight: 600;
  position: relative;
  text-align: left;
  width: 24%;

  &.is-hit {
    background: $primary;
    border-color: lighten($primary, 14%);
    color: $on-dark-strong;

    .plan-audience {
      color: $on-dark-body;
    }

    .plan-name {
      color: $on-dark-strong;
    }

    .plan-blurb {
      color: $on-dark-body;
    }

    .plan-icon {
      background: $ground;
      color: $primary;
    }
  }

  .plan-badge {
    background: $secondary;
    border-radius: $r-xs;
    box-shadow: 0 0 1rem rgba($secondary, .55);
    color: $on-dark-strong;
    font-family: $headings-font-family;
    font-size: 0.72rem;
    font-weight: 600;
    left: 50%;
    letter-spacing: 0.02em;
    line-height: 1;
    padding: 0.4rem 0.75rem;
    position: absolute;
    top: 0;
    transform: translate(-50%, -50%);
    transition: box-shadow $dur $ease;
    white-space: nowrap;
    z-index: 2;
  }

  /* Hovering a plan header lifts its icon and pushes the badge's glow out, so
     the column the pointer is over reads as the one being considered. */
  &:hover {
    .plan-icon {
      transform: translateY(-2px) scale(1.06);
    }

    .plan-badge {
      box-shadow: 0 0 1.4rem rgba($secondary, .85);
    }
  }

  .plan-meta-inner {
    display: flex;
    flex-direction: column;
    text-align: center;
  }

  .plan-head {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    margin-bottom: 0.75rem;
  }

  .plan-icon {
    align-items: center;
    background: $primary;
    border-radius: $r-md;
    color: $on-dark-strong;
    display: inline-flex;
    flex-shrink: 0;
    height: 2.65rem;
    justify-content: center;
    transition: transform $dur-quick $ease;
    width: 2.65rem;

    svg {
      height: 1.05rem;
      width: 1.05rem;
    }
  }

  .plan-head-copy {
    min-width: 0;
    text-align: left;
  }

  .plan-audience {
    color: $text-muted;
    display: block;
    font-family: $font-family-sans-serif;
    font-size: 0.78rem;
    font-weight: 400;
    line-height: 1.3;
    margin-bottom: 0.15rem;
  }

  .plan-name {
    color: $text-heading;
    display: block;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .plan-blurb {
    color: $text-muted;
    font-family: $font-family-sans-serif;
    font-size: 0.84rem;
    font-weight: 400;
    line-height: 1.45;
    margin: 0;
  }
}

thead tr.plan-meta th:not(.plan-corner) {
  padding-bottom: 1.35rem;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-top: 1.75rem;
  vertical-align: top;
}

thead tr.plan-meta th:nth-child(2) {
  border-top-left-radius: $r-lg;
}

thead tr.plan-meta th:last-child {
  border-top-right-radius: $r-lg;
}

thead th.plan-corner {
  background: transparent;
  border: 0;
  padding: 0;
  width: 28%;
}

tbody th {
  background: $ground;
  color: $text-strong;
  font-family: $headings-font-family;
  font-weight: 600;
  text-align: left;
  width: 28%;

  strong {
    display: block;
    font-weight: 600;
  }

  span {
    color: $text-muted;
    display: block;
    font-family: $font-family-sans-serif;
    font-size: 0.8rem;
    font-weight: 400;
    line-height: 1.4;
    margin-top: 0.2rem;
  }
}

tbody tr:first-child th {
  border-top: 2px solid $line-blue;
  border-top-left-radius: $r-lg;
}

tbody tr:last-child th {
  border-bottom-left-radius: $r-lg;
}

tbody tr:last-child td:last-child {
  border-bottom-right-radius: $r-lg;
}

td {
  color: $text-body;
  text-align: center;
  vertical-align: middle;

  &.is-hit {
    background: $primary;
    border-color: lighten($primary, 14%);
    color: $on-dark-strong;

    .plan-mark--yes {
      background: $ground;
      color: $primary;
    }

    .plan-mark--no {
      background: $on-dark-line-strong;
      color: $on-dark-strong;
    }
  }
}

.plan-mark {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  height: 1.55rem;
  justify-content: center;
  transition: transform $dur-quick $ease;
  width: 1.55rem;

  svg {
    height: 0.72rem;
    width: 0.72rem;
  }

  &--yes {
    background: $primary;
    color: $on-dark-strong;
  }

  &--no {
    background: $line-strong;
    color: $on-dark-strong;
  }
}

.plan-cell-text {
  display: inline-block;
  line-height: 1.35;
  white-space: pre-line;

  &.is-accent {
    color: $primary;
    font-family: $headings-font-family;
    font-weight: 700;
  }
}

.plan-price-row {
  td {
    padding-bottom: 1.15rem;
    padding-top: 1.15rem;
  }

  .plan-cost {
    text-align: center;
  }

  .plan-price,
  .plan-unit {
    display: block;
  }

  .plan-price {
    color: $primary;
    font-family: $headings-font-family;
    font-size: 2.15rem;
    font-weight: 700;
    letter-spacing: -0.045em;
    line-height: 1.1;
    white-space: nowrap;
  }

  .plan-unit {
    color: $text-muted;
    font-size: 0.8rem;
    line-height: 1.4;
    margin-top: 0.35rem;
  }

  /* A step down from the unit, so the alternate price reads as a footnote to the
     headline rather than a second offer. */
  .plan-alt {
    color: $text-muted;
    display: block;
    font-size: 0.72rem;
    line-height: 1.4;
    margin-top: 0.15rem;
  }

  td.is-hit {
    .plan-price {
      color: $on-dark-strong;
    }

    .plan-unit {
      color: $on-dark-body;
    }

    .plan-alt {
      color: $on-dark-body;
    }
  }
}

.plan-remarks {
  color: $text-muted;
  font-size: 0.78rem;
  line-height: 1.5;
  list-style: none;
  margin: $plan-remarks-gap 0 0;
  padding: 0 0.15rem;

  li {
    padding-left: 0.9rem;
    position: relative;

    &::before {
      content: '*';
      left: 0;
      position: absolute;
      top: 0;
    }

    + li {
      margin-top: 0.15rem;
    }
  }
}

/* Phones: the reader chooses one plan, so the table shows the label column and
   that plan only. Everything desktop-sized for four columns is released here. */
@media (max-width: 767.98px) {
  /* Only the chosen plan column is rendered, so this hides the rest. */
  .is-inactive-col {
    display: none;
  }

  /* The table's corners are hung on position, not on what is visible: the top
     left is `thead tr.plan-meta th:nth-child(2)`, the top right `th:last-child`
     and the bottom right `td:last-child` in the last row. Once two of the three
     plan columns are hidden, the column that survives is usually none of those
     cells, so it kept one rounded corner and squared off everywhere else.

     The surviving column is the table's right edge on a phone, so it owns both
     top corners and the bottom right. Same specificity as the rules it corrects
     (three elements, two classes each), so it has to stay below them. */
  thead tr.plan-meta th.is-active-col {
    border-left: 2px solid $line-blue;
    border-top-left-radius: $r-lg;
    border-top-right-radius: $r-lg;
  }

  /* The recommended column rules its own edges in a lighter blue, so its left
     edge has to take that colour rather than the label column's $line-blue. */
  thead tr.plan-meta th.is-active-col.is-hit {
    border-left-color: lighten($primary, 14%);
  }

  tbody tr:last-child td.is-active-col {
    border-bottom-right-radius: $r-lg;
  }

  /* Released so the label column and the chosen plan column share the row
     rather than keeping the desktop 28% / 24% splits. */
  thead th,
  tbody th,
  thead th.plan-corner {
    width: auto;
  }
}

/* The plan selector, shown only where the table is not. The switch itself is
   the shared SegmentedSwitch; this wrapper only sets it off from the table and
   takes it out of the desktop layout, where all four columns are already on
   screen. It is a wrapper rather than a class on the component so the rule
   cannot end up in a specificity contest with the component's own display. */
.plan-switch {
  margin-bottom: 1.35rem;

  @media (min-width: 768px) {
    display: none;
  }
}

/* Phones: the cell padding and the price step are sized for a desktop grid.
   Trimming them, and stepping the price down, is what makes the two columns that
   remain readable. */
@media (max-width: 575.98px) {
  th,
  td {
    padding: 0.6rem 0.7rem;
  }

  .plan-price-row .plan-price {
    font-size: 1.6rem;
  }
}
</style>

<script>
import { faBullhorn, faBullseye, faChartLine, faCheck, faGlobe, faMagnifyingGlass, faPenRuler, faRocket, faTimes } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import SectionTitle from '@/Components/SectionTitle.vue';
import SegmentedSwitch from '@/Components/SegmentedSwitch.vue';

FontAwesomeLibrary.add(faBullhorn, faBullseye, faChartLine, faCheck, faGlobe, faMagnifyingGlass, faPenRuler, faRocket, faTimes);

export default {
  components: {
    SectionTitle,
    SegmentedSwitch
  },

  data() {
    return {
      /* Which plan the table shows below md. The desktop table shows all of them,
         so this only chooses which column survives the narrow layout. */
      activeIndex: 0
    };
  },

  props: {
    title: {
      type: String,
      required: true
    },
    kicker: {
      type: String,
      default: ''
    },
    lede: {
      type: String,
      default: ''
    },
    columns: {
      type: Array,
      required: true
    },
    rows: {
      type: Array,
      required: true
    },
    fine: {
      type: [String, Array],
      default: () => []
    },
    remarks: {
      type: Array,
      default: () => []
    },
    priceLabel: {
      type: String,
      default: ''
    },
    priceHint: {
      type: String,
      default: ''
    },
    blockId: {
      type: String,
      default: ''
    }
  },

  computed: {
    /* The switch is labelled with the plan names, not the prices, so the labels
       stay put as the reader moves between segments. */
    columnNames() {
      return this.columns.map(function (column) {
        return column.name;
      });
    },

    remarksList() {
      var source = this.remarks.length ? this.remarks : this.fine;

      return [].concat(source).filter(Boolean);
    }
  },

  created() {
    /* Open on the recommended plan rather than the first column, because the
       filled column is the one the section is steering the reader to. */
    var hit = this.columns.findIndex(function (column) {
      return column.hit;
    });

    if (hit > -1) {
      this.activeIndex = hit;
    }
  },

  methods: {
    /* One place that names a plan column's state, so the plan header, the price
       row and the feature rows cannot disagree about which one is shown. */
    colClass(index, column) {
      var shown = index === this.activeIndex;

      return {
        'is-hit': !!(column && column.hit),
        'is-active-col': shown,
        'is-inactive-col': !shown
      };
    }
  }
};
</script>
